import { Badge, Email } from "@mui/icons-material";
import BusinessIcon from "@mui/icons-material/Business";
import { Box, Divider, Tooltip, Typography } from "@mui/material";

import { ISOStringToLocalReadableTimestamp, userHasNeverLoggedIn } from "utils";

interface Props {
  roles: UserRoleInfo[];
  title: string;
}

interface IconTextProps {
  icon: React.ReactNode;
  text: string;
}
const WARNING_TABLE_CELL_ROW = "#fce7c2";

function IconText(props: IconTextProps) {
  const { icon, text } = props;
  return (
    // alignItems flex-start so a wrapped email lines up under itself rather
    // than centring against the icon; minWidth lets the text wrap inside the
    // flex row instead of overflowing it.
    <Box display="flex" alignItems="flex-start">
      <Box display="flex" flexShrink={0}>
        {icon}
      </Box>
      <Typography ml="0.5rem" variant="body1" minWidth={0}>
        {text}
      </Typography>
    </Box>
  );
}

export function CompanyDetailRoles(props: Props) {
  const { roles, title } = props;

  return (
    <Box
      borderRadius={1}
      borderColor="gray"
      sx={{
        // Uncapped on a small screen: a nested scroll region inside the page
        // scroll clips the last member mid-row and is easy to miss. On a wider
        // screen the lists sit side by side, where a cap keeps the row tidy.
        maxHeight: { xs: "none", sm: "50vh" },
        // Full width on a narrow screen, side by side once there is room for
        // two. minWidth lets the flex item shrink instead of forcing the row
        // wider than the viewport, which is what pushed long emails outside it.
        flex: "1 1 20rem",
        minWidth: 0,
        overflowY: "auto",
        overflowWrap: "anywhere",
        borderWidth: 1,
        borderStyle: "solid",
        pl: "1rem",
        pr: "1rem",
      }}
    >
      <Box display="inline-flex" mt="1rem">
        <Typography variant="h5">
          {title}
          <Divider />
        </Typography>
      </Box>
      {roles.map((role) => (
        <Box
          key={role.user.id}
          sx={{
            backgroundColor: userHasNeverLoggedIn(role)
              ? WARNING_TABLE_CELL_ROW
              : undefined,
          }}
        >
          <Tooltip
            title={
              userHasNeverLoggedIn(role)
                ? "This user has not activated their Evotrux account."
                : ""
            }
            followCursor
          >
            <Box my="1rem">
              <IconText
                icon={<Badge />}
                text={`${role.user.first_name} ${role.user.last_name}`}
              />
              <IconText icon={<Email />} text={role.user.email} />
              {role?.branch?.name && (
                <IconText icon={<BusinessIcon />} text={role.branch.name} />
              )}
              <Typography variant="body2" color="text.secondary">
                Account created:{" "}
                {ISOStringToLocalReadableTimestamp(role.user.created_at)}
              </Typography>
            </Box>
          </Tooltip>
          <Divider />
        </Box>
      ))}
    </Box>
  );
}
